import { Badge, Box, Stack, Text } from '@chakra-ui/react';
import {
  BackofficeActivitiesOrderItemWithVoucher,
  VOUCHER_REWARD_TYPE,
} from '@diamond/shared/types';
import { PromoPrice } from '@diamond/shared/ui';

import { ColumnComponentProps } from '../../../types/activity-column-types';
import {
  calculateVoucherDiscount,
  formatCurrency,
} from '../../../utils/activity-formatters';

interface Props
  extends ColumnComponentProps<
    BackofficeActivitiesOrderItemWithVoucher,
    number
  > {
  isVoucherEnabled?: boolean;
}

export function PriceColumn({ info, isVoucherEnabled = false }: Props) {
  const formatCurrencyNoDecimals = (value: number) =>
    `Rp${Math.trunc(value).toLocaleString('id-ID')}`;

  const quantityItem = info.row?.original?.quantity_free_goods;
  const item = info.row?.original;

  // Safe access with fallbacks
  if (!item) {
    return <Text>-</Text>;
  }

  let finalPrice = info.getValue() ?? 0;
  let originalPrice = info.getValue() ?? 0;
  let discountAmount: number | undefined = item.deduction_amount;

  if (info.row.original.is_discount) {
    finalPrice = info.row.original.price_after_discount ?? finalPrice;
  }

  if (isVoucherEnabled && item.has_voucher_discount) {
    const voucherDiscountInfo = {
      hasVoucherDiscount: item.has_voucher_discount,
      voucherDiscountPercentage: item.voucher_discount_percentage,
      voucherDiscountAmount: item.voucher_discount_amount,
    };

    const result = calculateVoucherDiscount(finalPrice, voucherDiscountInfo);
    finalPrice = result.finalPrice;
    originalPrice = result.originalPrice;
    discountAmount = result.discountAmount;
  }

  // Use voucher_reward_type for explicit check (cleaner than inferring from fields)
  const hasNominalVoucher =
    isVoucherEnabled &&
    item.voucher_reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT;

  const isPromoNominalDiscount =
    info.row.original.is_discount &&
    !item.has_voucher_discount &&
    (discountAmount === undefined || discountAmount === null) &&
    info.row.original.price_after_discount !== undefined &&
    info.row.original.price_after_discount !== originalPrice;

  const promoNominalDiscountAmount = isPromoNominalDiscount
    ? originalPrice - (info.row.original.price_after_discount ?? originalPrice)
    : 0;

  return (
    <Stack>
      <Text fontSize="sm" fontWeight="400" color="#373F41">
        {info.row.original.is_discount || item.has_voucher_discount
          ? formatCurrency(finalPrice)
          : quantityItem
          ? undefined
          : formatCurrency(info.getValue())}
      </Text>
      {info.row.original.is_discount || item.has_voucher_discount ? (
        hasNominalVoucher || isPromoNominalDiscount ? (
          <Box display="flex" gap="1" alignItems="center">
            <Text
              color="gray.75"
              textDecoration="line-through"
              fontSize="sm"
              fontWeight="400"
            >
              {formatCurrency(originalPrice)}
            </Text>
            <Badge
              color="#FF0000"
              bgColor="#FFDADA"
              rounded="4px"
              fontSize="sm"
              fontWeight="400"
            >
              -
              {formatCurrencyNoDecimals(
                hasNominalVoucher && item.voucher_discount_amount
                  ? item.voucher_discount_amount
                  : promoNominalDiscountAmount
              )}
            </Badge>
          </Box>
        ) : (
          <PromoPrice
            formattedPrice={formatCurrency(originalPrice)}
            discountAmount={discountAmount}
            isActive
            isFreeGoods={false}
          />
        )
      ) : quantityItem ? (
        <PromoPrice
          formattedPrice={formatCurrency(info.row.original.price)}
          isFreeGoods
        />
      ) : (
        info.row.getIsSelected() === true && <div style={{ height: '20px' }} />
      )}
    </Stack>
  );
}
